import { useFirestore } from "hooks/firestore/v2/useFirestore";
import { bettingEvent } from "hooks/firestore/v2/models/bettingEvent";
import {
  limit,
  orderBy,
  type QueryConstraint,
  where,
} from "firebase/firestore";
import { defaultPageSize } from "utilities/display";
import { useUserEventFilters } from "hooks/firestore/useUserEventFilters";
import useHasPermission, { usePermissions } from "hooks/useHasPermission";
import { useMemo } from "react";
import { useCampaigns } from "hooks/firestore/v2/user/useCampaigns";
import { useTokens } from "hooks/firestore/v2/user/useTokens";

import { processBettingEvent } from "./shared";

type BettingEventsOptions = {
  page: number;
  titles?: string[];
  tournaments?: string[];
  tab: "next" | "outrights";
  hub?: "sports" | "esports";
  sgm: boolean;
};

const getBettingQuery = ({
  eventFilters,
  page,
  titles,
  tournaments,
  tab,
  hub,
  canViewLiveBets,
  sgm = false,
}: BettingEventsOptions & {
  canViewLiveBets: boolean;
  eventFilters: string[];
}): QueryConstraint[] => {
  const queries: QueryConstraint[] = [
    where("tab", "==", tab === "next" ? "next" : "outrights"),
    where("hub", "==", hub === "esports" ? "esports" : "sports"),
    where("visible", "==", true),
    limit(page * defaultPageSize()),
  ];

  if (sgm) {
    queries.push(where("attributes.sgm", "==", true));
  }

  if (tab === "outrights") {
    queries.push(
      where("activeMarketCount", ">", 0),
      orderBy("activeMarketCount", "asc"),
    );
  }

  if (tab === "next") {
    queries.push(orderBy("nextBetStop", "asc"));
  } else {
    queries.push(orderBy("scheduledStartTime", "asc"));
  }

  if (!canViewLiveBets && tab === "next") {
    // only include prematch if we can't livebet
    queries.push(where("mainMarket.bettingType", "==", "Prematch"));
  }

  if (eventFilters.length) {
    queries.push(where("filters", "array-contains-any", eventFilters));
  }

  if (tournaments?.length) {
    queries.push(where("tournamentId", "in", tournaments));
  } else if (titles?.length) {
    queries.push(
      where(
        "sport",
        "in",
        titles.map((title) => title.toUpperCase()),
      ),
    );
  }

  return queries;
};

const useBettingEvents = (options: BettingEventsOptions) => {
  const eventFilters = useUserEventFilters();
  const canViewLiveBets = useHasPermission("viewLiveMarkets");
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();
  const { tokens } = useTokens();

  const {
    data,
    error,
    isLoading: eventsLoading,
  } = useFirestore("/bettingEvents/", bettingEvent.array(), {
    query: getBettingQuery({ eventFilters, canViewLiveBets, ...options }),
  });

  const processedData = useMemo(() => {
    if (!data || !codes) return;

    return data
      .map((event) => processBettingEvent(event, codes, tokens, permissions))
      .filter((event) =>
        Object.values(event.mainMarket?.outcomes || {}).every(
          (outcome) => outcome.odds > 1,
        ),
      );
  }, [data]);

  return {
    error,
    events: processedData,
    isLoading: eventsLoading || codesLoading,
  };
};

type Event = ReturnType<typeof processBettingEvent>;

export { useBettingEvents, type Event };
